import * as Yup from 'yup';
import { DeepPartial, FieldNamesMarkedBoolean } from 'react-hook-form';
import {
  AmenityChargeFrequency,
  AmenityType,
  PropertyAmenity,
} from 'models/Amenity';
import { PropertyRule, RuleType } from 'models/Rule';
import { ALL_CATEGORIZED_AMENITIES_ITEMS } from './CategorizedAmenities.constants';

const amenityItemSchema = () =>
  Yup.object({
    chargeFrequency: Yup.mixed<AmenityChargeFrequency>()
      .oneOf([null, ...Object.values(AmenityChargeFrequency)])
      .transform((value) => (value === '' ? null : value))
      .nullable(),
    description: Yup.string().nullable(),
    price: Yup.number()
      .useNaNAsNull()
      .useNegativeZeroAsNegative()
      .moreThan(0)
      .nullable(),
    value: Yup.boolean().required(),
  });

export const categorizedAmenitiesSchema = () =>
  Yup.object({
    amenities: Yup.object(
      ALL_CATEGORIZED_AMENITIES_ITEMS.reduce(
        (acc, type) => ({
          ...acc,
          [type]: amenityItemSchema(),
        }),
        {} as {
          [key in AmenityType | RuleType]: ReturnType<typeof amenityItemSchema>;
        },
      ),
    ),
  });

export type CategorizedAmenitiesFormValues = DeepPartial<
  Yup.InferType<ReturnType<typeof categorizedAmenitiesSchema>>
>;

export type CategorizedAmenitiesFormDirtyFields = Partial<
  Readonly<FieldNamesMarkedBoolean<CategorizedAmenitiesFormValues>>
>;

export function getFormDefaultValues(
  propertyAmenities: PropertyAmenity[],
  propertyRules: PropertyRule[],
): CategorizedAmenitiesFormValues {
  const baseValues = ALL_CATEGORIZED_AMENITIES_ITEMS.reduce(
    (acc, amenity) => ({
      ...acc,
      [amenity]: {
        description: null,
        value: false,
      },
    }),
    {},
  );

  const allItems =
    propertyAmenities || propertyRules
      ? [...(propertyAmenities ?? []), ...(propertyRules ?? [])]
      : undefined;

  return {
    amenities: allItems?.reduce(
      (acc, { description, ...item }) => ({
        ...acc,
        ['amenity' in item ? item.amenity : item.rule]: {
          description,
          value: true,
        },
      }),
      baseValues,
    ),
  };
}
